import { useMemo, useState } from "react"
import { router } from "@inertiajs/react"
import {
  AlertTriangle,
  ChevronRight,
  Clock,
  LayoutGrid,
  List,
  QrCode,
  Search,
  Users,
  Utensils,
} from "lucide-react"
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { MainContainer } from "@/components/ui/container"
import { FilterChip } from "@/components/ui/filter-chip"
import { PageHeader } from "@/components/ui/headers"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import type { StaffEntryRequest } from "@/features/staff/entry-request-panel"
import {
  ALERT_PULSE_CLASS,
  alertMessageFor,
  operationalStatusDot,
  operationalStatusLabelKey,
  type AlertReason,
  type OperationalStatus,
} from "@/features/staff/table-status"
import { cn } from "@/lib/utils"
import { intlLocale, t } from "@/lib/i18n"
import { normalizeForSearch } from "@/lib/search"

type TableAction = {
    label: string
    value: string
    disabled: boolean
    primary: boolean
    kind: "navigate" | "event" | "invite"
    href?: string
    event?: string
}
type TableAccountOrder = {
    id: number
    name: string
    quantity: number
    status: string
    statusLabel: string
    lineTotal: string
    currency: string
    customerName: string
    updatedAt: string
}
type TableAccountBill = {
    id: number
    status: string
    statusLabel: string
    ordersCount: number
    total: string
    currency: string
    pendingPaymentAttempt: {
        id: number
        status: string
        paymentMethod: string
        totalAmount: string
        /** When the diner got stuck at the payment step — drives the payment_pending alert. */
        createdAt?: string
    } | null
    orders: TableAccountOrder[]
}
type ActiveInvite = {
    id: number
    token: string
    expiresAt: string
    maxUses: number
    usedCount: number
    url: string
}
export type OverviewTable = {
    id: number
    externalId: string
    name: string
    seats: number
    status: string
    statusLabel: string
    operationalStatus: OperationalStatus
    alert: boolean
    /** Every reason the table needs a human, most urgent first. `alert` is "any of these". */
    alertReasons: AlertReason[]
    activeDinersCount: number
    activeOrdersCount: number
    activeBillsCount: number
    activeInvite: ActiveInvite | null
    /** Pending requests to be let in, most-recent-last. Empty when the door is quiet. */
    entryRequests: StaffEntryRequest[]
    accountWorkspace: {
        billCount: number
        orderCount: number
        visibleBills: TableAccountBill[]
    }
    actions: TableAction[]
}
type Props = {
    tables: OverviewTable[]
    locationName?: string | null
    isManager?: boolean
}
type StatusFilter = "all" | "alerts" | "ready" | "paying" | "free"
type ViewMode = "grid" | "list"
type DerivedTable = OverviewTable & {
    operationalLabel: string
    total: number
    currency: string
    itemsCount: number
    alertMessage: string | null
    /** A diner rang for a waiter — the only alert staff can answer with one tap. */
    waiterRequested: boolean
}
const I18N_BASE = "ui.react.features.staff.tables_overview"
function formatMoney(amount: number, currency = "MXN") {
    return new Intl.NumberFormat(intlLocale(), {
        style: "currency",
        currency: currency.toUpperCase(),
    }).format(Number.isFinite(amount) ? amount : 0)
}
export function summarizeOverviewAccount(bills: TableAccountBill[]) {
    const orders = bills.flatMap((bill) => bill.orders)
    const nonCancelledOrders = orders.filter((order) => order.status !== "cancelled")
    return {
        amount: bills.reduce((sum, bill) => sum + Number(bill.total || 0), 0),
        currency: bills[0]?.currency ?? "MXN",
        itemsCount: nonCancelledOrders.reduce((sum, order) => sum + order.quantity, 0),
    }
}
export function deriveOverviewTable(table: OverviewTable): DerivedTable {
    const account = summarizeOverviewAccount(table.accountWorkspace.visibleBills)
    const reasons = table.alertReasons ?? []
    return {
        ...table,
        operationalLabel: statusLabel(table.operationalStatus),
        total: account.amount,
        currency: account.currency,
        itemsCount: account.itemsCount,
        alertMessage: alertMessageFor(reasons),
        waiterRequested: reasons.includes("waiter_requested"),
    }
}
/**
 * Whether a table answers the waiter's search term, ignoring case and accents —
 * "terraza" has to find "Terraza" and "salon" has to find "Salón", because a
 * waiter on the floor is typing fast on a phone, not proofreading.
 *
 * `normalizedQuery` arrives already folded so the term is folded once per
 * keystroke instead of once per table. An empty term matches everything.
 */
export function overviewTableMatchesQuery(table: Pick<DerivedTable, "name" | "operationalLabel" | "alertMessage">, normalizedQuery: string): boolean {
    if (!normalizedQuery)
        return true
    const haystack = normalizeForSearch(`${table.name} ${table.operationalLabel} ${table.alertMessage ?? ""}`)
    return haystack.includes(normalizedQuery)
}
function statusLabel(status: OperationalStatus): string {
    return t(operationalStatusLabelKey(status))
}
export function TablesOverview({ tables, locationName, isManager = false }: Props) {
    const [viewMode, setViewMode] = useState<ViewMode>("grid")
    const [statusFilter, setStatusFilter] = useState<StatusFilter>("all")
    const [query, setQuery] = useState("")
    const derived = useMemo(() => tables.map(deriveOverviewTable), [tables])
    const filtered = useMemo(() => {
        const normalizedQuery = normalizeForSearch(query.trim())
        return derived.filter((table) => {
            if (statusFilter === "alerts" && !table.alert)
                return false
            if (statusFilter === "ready" && table.operationalStatus !== "ready")
                return false
            if (statusFilter === "paying" && table.operationalStatus !== "paying")
                return false
            if (statusFilter === "free" && table.operationalStatus !== "free")
                return false
            if (!overviewTableMatchesQuery(table, normalizedQuery))
                return false
            return true
        })
    }, [derived, statusFilter, query])
    const attentionTables = useMemo(() => derived.filter((table) => table.alert), [derived])
    // Selecting a table navigates. The overview used to answer with a drawer that
    // summarised the table and then offered "Ir a la mesa" as its main button —
    // an interstitial in front of the screen that holds the same summary in its
    // context rail, plus the account, the menu and every action the drawer
    // carried. Two taps to reach the work, and a second place the same table
    // context had to be kept true.
    const handleGoToTable = (table: DerivedTable) => {
        router.visit(`/tables/${table.externalId}`)
    }
    return (<MainContainer size="md">
      <div className="space-y-4 pb-6" data-testid="tables-overview-content">
        <PageHeader title={t(`${I18N_BASE}.tables`)} description={locationName ?? undefined} actions={<div className="relative w-full sm:w-[260px]">
            <Label htmlFor="tables-overview-search" className="sr-only">{t(`${I18N_BASE}.search_table_status_event`)}</Label>
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"/>
            <Input id="tables-overview-search" type="search" placeholder={t(`${I18N_BASE}.search_table_status_event`)} value={query} onChange={(event) => setQuery(event.target.value)} className="pl-9"/>
          </div>}/>

        <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg bg-muted p-0.5" role="group" aria-label={t(`${I18N_BASE}.view_mode`)}>
            <SegmentButton active={viewMode === "grid"} onClick={() => setViewMode("grid")} icon={<LayoutGrid className="size-3.5"/>} label={t(`${I18N_BASE}.grid`)}/>
            <SegmentButton active={viewMode === "list"} onClick={() => setViewMode("list")} icon={<List className="size-3.5"/>} label={t(`${I18N_BASE}.list`)}/>
          </div>

          <span className="h-6 w-px bg-border" aria-hidden="true"/>

          <FilterChip active={statusFilter === "all"} onClick={() => setStatusFilter("all")}>{t(`${I18N_BASE}.all`)}</FilterChip>
          <FilterChip active={statusFilter === "alerts"} onClick={() => setStatusFilter("alerts")} tone="danger">
            {t(`${I18N_BASE}.attention_count`, { count: attentionTables.length })}
          </FilterChip>
          <FilterChip active={statusFilter === "ready"} onClick={() => setStatusFilter("ready")} tone="available">{t(`${I18N_BASE}.ready_serve_2`)}</FilterChip>
          <FilterChip active={statusFilter === "paying"} onClick={() => setStatusFilter("paying")} tone="occupied">{t(`${I18N_BASE}.charge`)}</FilterChip>
          <FilterChip active={statusFilter === "free"} onClick={() => setStatusFilter("free")} tone="available">{t(`${I18N_BASE}.free_2`)}</FilterChip>
        </div>

        {attentionTables.length > 0 && statusFilter !== "alerts" ? (<Card density="compact">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="size-4 text-destructive"/>
                {t(`${I18N_BASE}.need_attention_now`)}
              </CardTitle>
              <CardAction className="text-sm font-medium tabular-nums text-muted-foreground">
                {attentionTables.length}
              </CardAction>
            </CardHeader>
            <CardContent className="p-0">
              <ul className="divide-y">
                {attentionTables.map((table) => (<li key={table.id}>
                    <button type="button" onClick={() => handleGoToTable(table)} className="flex w-full items-center gap-2 px-4 py-2 text-left transition-colors hover:bg-muted/50" data-testid={`attention-card-${table.id}`}>
                      <span className={cn("h-2 w-2 shrink-0 rounded-full bg-destructive", ALERT_PULSE_CLASS)}/>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{table.name}</p>
                        <p className="truncate text-xs text-muted-foreground">{table.alertMessage}</p>
                      </div>
                      <p className="shrink-0 text-sm font-medium tabular-nums">
                        {table.total > 0 ? formatMoney(table.total, table.currency) : "—"}
                      </p>
                    </button>
                  </li>))}
              </ul>
            </CardContent>
          </Card>) : null}

        {derived.length === 0 ? (<div className="rounded-xl border bg-card px-6 py-16 text-center shadow-sm">
            <p className="text-sm font-semibold">{t(`${I18N_BASE}.no_tables`)}</p>
            <p className="mt-1 text-sm text-muted-foreground">{t(`${I18N_BASE}.${isManager ? "no_tables_manager_hint" : "no_tables_staff_hint"}`)}</p>
          </div>) : filtered.length === 0 ? (<div className="rounded-xl border bg-card px-6 py-16 text-center shadow-sm">
            <p className="text-sm font-semibold">{t(`${I18N_BASE}.no_matches`)}</p>
            <p className="mt-1 text-sm text-muted-foreground">{t(`${I18N_BASE}.no_matches_hint`)}</p>
          </div>) : viewMode === "grid" ? (<div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(240px,1fr))]" data-testid="tables-grid-view">
            {filtered.map((table) => (<TableCard key={table.id} table={table} onOpen={() => handleGoToTable(table)}/>))}
          </div>) : (<TableListView tables={filtered} onOpen={handleGoToTable}/>)}
        </div>
      </div>

    </MainContainer>)
}
function SegmentButton({ active, onClick, icon, label, }: {
    active: boolean
    onClick: () => void
    icon: React.ReactNode
    label: string
}) {
    return (<button type="button" onClick={onClick} className={cn("inline-flex h-10 items-center gap-1.5 rounded-md px-3 text-xs font-medium transition-colors md:h-7", active
            ? "bg-background text-foreground shadow-sm"
            : "text-muted-foreground hover:text-foreground")} aria-pressed={active}>
      {icon}
      {label}
    </button>)
}
function StatusDotLabel({ status, label, className }: {
    status: OperationalStatus
    label: string
    className?: string
}) {
    return (<span className={cn("inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground", className)}>
      <span className={cn("h-2 w-2 shrink-0 rounded-full", operationalStatusDot(status))}/>
      {label}
    </span>)
}
function TableCard({ table, onOpen }: {
    table: DerivedTable
    onOpen: () => void
}) {
    const isFree = table.operationalStatus === "free"
    return (<button type="button" onClick={onOpen} className={cn("group flex min-h-[150px] flex-col gap-2.5 rounded-xl border bg-card p-4 text-left shadow-sm transition-colors hover:border-ring/60", table.alert && "border-destructive/40")} data-testid={`table-card-${table.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-lg font-semibold leading-none tracking-tight">
            {table.name}
          </p>
          <p className="mt-1.5 text-xs text-muted-foreground">
            {t(`${I18N_BASE}.seats_count`, { count: table.seats })}
          </p>
        </div>
        <StatusDotLabel status={table.operationalStatus} label={table.operationalLabel}/>
      </div>

      {isFree ? (<div className="flex flex-1 flex-col items-center justify-center gap-1.5 py-2 text-center text-xs text-muted-foreground">
          <QrCode className="size-5 text-muted-foreground/50"/>
          <span>{t(`${I18N_BASE}.ready_occupy`)}</span>
        </div>) : (<>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1 tabular-nums">
              <Users className="size-3"/>
              {t(`${I18N_BASE}.seats_count`, { count: table.seats })}
            </span>
            <span className="size-0.5 rounded-full bg-muted-foreground/40"/>
            <span className="inline-flex items-center gap-1 tabular-nums">
              <Utensils className="size-3"/>
              {table.itemsCount} {t(`${I18N_BASE}.item`)}{table.itemsCount !== 1 ? "s" : ""}
            </span>
            {table.activeBillsCount > 0 ? (<>
                <span className="size-0.5 rounded-full bg-muted-foreground/40"/>
                <span className="inline-flex items-center gap-1 tabular-nums">
                  <Clock className="size-3"/>
                  {table.activeBillsCount} {t(`${I18N_BASE}.bill`)}{table.activeBillsCount !== 1 ? "s" : ""}
                </span>
              </>) : null}
          </div>

          <EventRow table={table}/>

          <div className="mt-auto flex items-end justify-between gap-2 pt-1">
            <span className="text-xs text-muted-foreground">{t(`${I18N_BASE}.bills_pending_count`, { count: table.accountWorkspace.billCount })}</span>
            <span className="text-sm font-semibold tabular-nums">
              {formatMoney(table.total, table.currency)}
            </span>
          </div>
        </>)}
    </button>)
}
function EventRow({ table }: {
    table: DerivedTable
}) {
    if (table.alert && table.alertMessage) {
        return (<p className="inline-flex items-center gap-1.5 text-xs font-medium text-destructive">
        <AlertTriangle className={cn("size-3 shrink-0", ALERT_PULSE_CLASS)}/>
        <span className="truncate">{table.alertMessage}</span>
      </p>)
    }
    if (table.operationalStatus === "cooking") {
        return (<p className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-status-occupied"/>
        <span className="truncate">{t(`${I18N_BASE}.ticket_kitchen`)}</span>
      </p>)
    }
    if (table.operationalStatus === "ready") {
        return (<p className="inline-flex items-center gap-1.5 text-xs font-medium text-status-available">
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-status-available"/>
        <span className="truncate">{t(`${I18N_BASE}.dish_ready`)}</span>
      </p>)
    }
    if (table.operationalStatus === "ordering") {
        return (<p className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-status-occupied"/>
        <span className="truncate">{t(`${I18N_BASE}.taking_order`)}</span>
      </p>)
    }
    return null
}
function TableListView({ tables, onOpen }: {
    tables: DerivedTable[]
    onOpen: (table: DerivedTable) => void
}) {
    return (<div className="rounded-xl border bg-card shadow-sm">
      <div className="hidden grid-cols-[60px_minmax(0,1fr)_120px_110px_minmax(0,160px)_70px_110px_28px] items-center gap-3 border-b px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground lg:grid">
        <div>{t(`${I18N_BASE}.table`)}</div>
        <div>{t(`${I18N_BASE}.capacity`)}</div>
        <div>{t(`${I18N_BASE}.status`)}</div>
        <div>{t(`${I18N_BASE}.bills`)}</div>
        <div>{t(`${I18N_BASE}.last_event`)}</div>
        <div className="text-center">{t(`${I18N_BASE}.items_ticket`)}</div>
        <div className="text-right">{t(`${I18N_BASE}.total`)}</div>
        <div />
      </div>
      {tables.map((table) => {
            const lastEvent = table.alertMessage
                ? table.alertMessage
                : table.operationalStatus === "cooking"
                    ? t(`${I18N_BASE}.ticket_kitchen`)
                    : table.operationalStatus === "ordering"
                        ? t(`${I18N_BASE}.taking_order`)
                        : t("ui.common.no_activity")
            return (<button key={table.id} type="button" onClick={() => onOpen(table)} className="flex w-full cursor-pointer items-center gap-3 border-t px-4 py-3 text-left transition-colors first-of-type:border-t-0 hover:bg-muted/50 lg:grid lg:grid-cols-[60px_minmax(0,1fr)_120px_110px_minmax(0,160px)_70px_110px_28px]" data-testid={`table-list-row-${table.id}`}>
          <span className="min-w-0 flex-1 lg:contents">
            <span className="flex items-center gap-2 lg:contents">
              {/* The alert mark lives INSIDE the name cell on purpose: this wrapper
                  becomes `contents` at lg, so a sibling here would claim a grid
                  column of its own and shift every column after it. */}
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold tracking-tight">
                {table.name}
                {table.alert ? (<AlertTriangle className={cn("size-3.5 shrink-0 text-destructive", ALERT_PULSE_CLASS)} aria-label={table.alertMessage ?? t(`${I18N_BASE}.requires_attention`)}/>) : null}
              </span>
              <span className="hidden min-w-0 lg:block">
                <span className="block truncate text-sm font-medium">{t(`${I18N_BASE}.seats_count`, { count: table.seats })}</span>
                <span className="block truncate text-xs text-muted-foreground">{t(`${I18N_BASE}.capacity`)}</span>
              </span>
              <StatusDotLabel status={table.operationalStatus} label={table.operationalLabel}/>
            </span>
            <span className="mt-0.5 block truncate text-xs text-muted-foreground lg:hidden">
              {t(`${I18N_BASE}.seats_count`, { count: table.seats })} · {t(`${I18N_BASE}.open_bills_count`, { count: table.activeBillsCount })} · {lastEvent}
            </span>
            <span className="hidden text-xs font-medium tabular-nums lg:block">
              {t(`${I18N_BASE}.open_bills_count`, { count: table.activeBillsCount })}
            </span>
            <span className="hidden truncate text-xs text-muted-foreground lg:block">
              {lastEvent}
            </span>
            <span className="hidden text-center text-xs tabular-nums lg:block">
              {table.itemsCount}
            </span>
          </span>
          <span className="shrink-0 text-right text-sm font-semibold tabular-nums">
            {table.total > 0 ? formatMoney(table.total, table.currency) : "—"}
          </span>
          <ChevronRight className="size-3.5 shrink-0 text-muted-foreground lg:justify-self-end"/>
        </button>)
        })}
    </div>)
}
